import { useNavigate } from 'react-router-dom';

import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import ClientFormFields from 'components/forms/ClientFormFields';

import useClient from 'hooks/useClient';
import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';
import usePageRoutes from 'hooks/usePageRoutes';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { CreateClientFormSchema, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const CreateClientForm: FunctionComponent<Props> = ({
    className = '',
    onCancelClose,
    onClose,
    requestingClose,
    testId = TEST_ID
}) => {
    const { createClient, isLoading } = useClient();
    const { CREATE } = useTranslation();
    const navigate = useNavigate();
    const { clientDetailsPage } = usePageRoutes();

    const form = useForm<CreateClientFormSchema>({
        mode: 'onChange'
    });

    const {
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose,
        onClose,
        requestingClose
    });

    const onSubmit = async (data: CreateClientFormSchema) => {
        const client = await createClient({ client: data });

        if (client) {
            onClose?.();
            navigate(clientDetailsPage(client.id));
        }
    };

    return (
        <VerticalContainer className={`${styles.createClientForm} ${className}`} testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form form={form}>
                    <ClientFormFields />

                    <FormSubmitPanel
                        buttonText={CREATE}
                        disabled={isLoading}
                        errors={errors}
                        onSubmit={handleSubmit(onSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default CreateClientForm;
